import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, MessageSquare, Plus } from 'lucide-react';
import { EVENT_TYPE_LABEL } from '@wulftime/core';
import { api } from '../../lib/api';
import { fmtDate, fmtDateTime, fmtTime, todayISO } from '../../lib/format';
import { Alert, Button, Card, EmptyState, ErrorBox, Field, Input, Modal, PageHeader, Select, Spinner, Tabs, Textarea, useToast } from '../../components/ui';
import { IncidentStatusBadge, RequestStatusBadge } from '../../components/status';

export function NewIncidentModal({ open, onClose, defaultType, employeeId }: { open: boolean; onClose: () => void; defaultType?: string; employeeId?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: types } = useQuery({ queryKey: ['incident-types'], queryFn: () => api.get<any[]>('/api/v1/incident-types') });
  const [typeCode, setTypeCode] = useState(defaultType ?? 'OTHER');
  const [workDate, setWorkDate] = useState(todayISO());
  const [description, setDescription] = useState('');
  const m = useMutation({
    mutationFn: () => api.post('/api/v1/incidents', { typeCode, workDate, description, employeeId }),
    onSuccess: () => {
      toast.success('Incidencia enviada.');
      qc.invalidateQueries({ queryKey: ['incidents'] });
      onClose();
    },
  });
  const selectable = (types ?? []).filter((t) => t.active && (employeeId || t.workerSelectable));
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nueva incidencia"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={m.isPending} disabled={description.trim().length < 5} onClick={() => m.mutate()}>
            Enviar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ErrorBox error={m.error} />
        <Field label="Tipo">
          {(id) => (
            <Select id={id} value={typeCode} onChange={(e) => setTypeCode(e.target.value)}>
              {selectable.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Fecha de la jornada">{(id) => <Input id={id} type="date" max={todayISO()} value={workDate} onChange={(e) => setWorkDate(e.target.value)} />}</Field>
        <Field label="Descripción" required>{(id) => <Textarea id={id} rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Explica qué ha ocurrido" />}</Field>
      </div>
    </Modal>
  );
}

export function WorkerRequests() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<'incidents' | 'corrections'>('incidents');
  const [newIncident, setNewIncident] = useState(params.get('new') === 'incident');
  const navigate = useNavigate();
  const incidents = useQuery({ queryKey: ['incidents', 'mine'], queryFn: () => api.get<any[]>('/api/v1/incidents?mine=true') });
  const corrections = useQuery({ queryKey: ['corrections', 'mine'], queryFn: () => api.get<any[]>('/api/v1/corrections?mine=true') });
  useEffect(() => {
    if (params.get('new')) setParams({}, { replace: true });
  }, [params, setParams]);
  return (
    <div>
      <PageHeader
        title="Solicitudes"
        description="Incidencias y correcciones de fichaje. Nunca modificas un registro directamente: tu responsable lo revisa."
        actions={
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setNewIncident(true)}>
            Nueva incidencia
          </Button>
        }
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'incidents', label: 'Incidencias', count: incidents.data?.length },
          { id: 'corrections', label: 'Correcciones', count: corrections.data?.length },
        ]}
      />
      {tab === 'incidents' &&
        (incidents.isLoading ? (
          <Spinner />
        ) : !incidents.data?.length ? (
          <Card>
            <EmptyState title="No tienes incidencias" />
          </Card>
        ) : (
          <ul className="space-y-2">
            {incidents.data.map((i) => (
              <li key={i.id}>
                <button onClick={() => navigate(`/app/requests/incidents/${i.id}`)} className="card flex w-full items-start justify-between gap-3 p-4 text-left hover:border-brand-300">
                  <div>
                    <p className="font-medium">{i.title}</p>
                    <p className="text-xs text-slate-500">
                      {i.workDate ? fmtDate(i.workDate) : fmtDateTime(i.createdAt)} · {i.origin === 'AUTO' ? 'Detectada automáticamente' : i.origin === 'WORKER' ? 'Creada por ti' : 'Creada por tu responsable'}
                    </p>
                  </div>
                  <IncidentStatusBadge status={i.status} />
                </button>
              </li>
            ))}
          </ul>
        ))}
      {tab === 'corrections' &&
        (corrections.isLoading ? (
          <Spinner />
        ) : !corrections.data?.length ? (
          <Card>
            <EmptyState title="No tienes solicitudes de corrección" description="Puedes solicitarlas desde el detalle de una jornada en el historial." />
          </Card>
        ) : (
          <ul className="space-y-2">
            {corrections.data.map((c) => (
              <li key={c.id}>
                <button onClick={() => navigate(`/app/requests/corrections/${c.id}`)} className="card flex w-full items-start justify-between gap-3 p-4 text-left hover:border-brand-300">
                  <div>
                    <p className="font-medium">
                      {{ ADD_EVENT: 'Añadir', CHANGE_TIME: 'Cambiar hora', VOID_EVENT: 'Anular' }[c.kind as string]}
                      {c.eventType ? ` · ${EVENT_TYPE_LABEL[c.eventType as keyof typeof EVENT_TYPE_LABEL]}` : ''} — {fmtDate(c.workDate)}
                    </p>
                    <p className="line-clamp-1 text-xs text-slate-500">{c.reason}</p>
                  </div>
                  <RequestStatusBadge status={c.status} />
                </button>
              </li>
            ))}
          </ul>
        ))}
      <NewIncidentModal open={newIncident} onClose={() => setNewIncident(false)} defaultType={params.get('type') ?? undefined} />
    </div>
  );
}

export function IncidentThread({ id, backTo }: { id: string; backTo: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({ queryKey: ['incident', id], queryFn: () => api.get(`/api/v1/incidents/${id}`) });
  const [comment, setComment] = useState('');
  const [decision, setDecision] = useState<{ to: string; label: string } | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['incident', id] });
    qc.invalidateQueries({ queryKey: ['incidents'] });
  };
  const addComment = useMutation({ mutationFn: () => api.post(`/api/v1/incidents/${id}/comments`, { body: comment }), onSuccess: () => (setComment(''), refresh()) });
  const transition = useMutation({
    mutationFn: (v: { to: string; comment?: string }) => api.post(`/api/v1/incidents/${id}/transition`, v),
    onSuccess: () => {
      setDecision(null);
      setComment('');
      toast.success('Incidencia actualizada');
      refresh();
    },
    onError: toast.error,
  });
  if (isLoading) return <Spinner />;
  const i = data;
  return (
    <div>
      <PageHeader
        back={
          <Link to={backTo} className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
            <ChevronLeft className="h-4 w-4" /> Volver
          </Link>
        }
        title={i.title}
        description={`${i.typeName} · ${i.employee ? `${i.employee.firstName} ${i.employee.lastName}` : ''}${i.workDate ? ` · jornada del ${fmtDate(i.workDate)}` : ''}`}
        actions={<IncidentStatusBadge status={i.status} />}
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="Descripción">
            <p className="whitespace-pre-line text-sm text-slate-700">{i.description || '—'}</p>
          </Card>
          <Card title="Conversación y cambios de estado" padded={false}>
            <ul className="divide-y divide-slate-100">
              {i.comments.map((c: any) => (
                <li key={c.id} className="px-5 py-3 text-sm">
                  <div className="flex justify-between gap-2 text-xs text-slate-500">
                    <span className="font-medium text-slate-700">{c.author ?? 'WulfTime'}</span>
                    <span>{fmtDateTime(c.createdAt)}</span>
                  </div>
                  {c.kind === 'STATUS_CHANGE' || c.kind === 'INFO_REQUEST' ? (
                    <p className="mt-1 text-xs text-slate-600">
                      Estado: {c.fromStatus} → <strong>{c.toStatus}</strong>
                    </p>
                  ) : null}
                  {c.body && <p className="mt-1 whitespace-pre-line text-slate-800">{c.body}</p>}
                </li>
              ))}
            </ul>
            {i.status !== 'CLOSED' && (
              <div className="space-y-2 border-t border-slate-100 p-4">
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Escribe un comentario…" aria-label="Comentario" />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" icon={<MessageSquare className="h-4 w-4" />} disabled={!comment.trim()} loading={addComment.isPending} onClick={() => addComment.mutate()}>
                    Comentar
                  </Button>
                  {i.isOwner && ['DRAFT', 'INFO_REQUESTED'].includes(i.status) && (
                    <Button size="sm" onClick={() => transition.mutate({ to: 'SUBMITTED', comment: comment || undefined })}>
                      {i.status === 'DRAFT' ? 'Enviar' : 'Responder y reenviar'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>
        {(i.canReview || i.canApprove) && (
          <Card title="Resolver">
            <div className="flex flex-col gap-2">
              {i.canReview && i.status === 'SUBMITTED' && (
                <Button variant="secondary" onClick={() => transition.mutate({ to: 'IN_REVIEW' })}>
                  Marcar en revisión
                </Button>
              )}
              {i.canReview && ['SUBMITTED', 'IN_REVIEW'].includes(i.status) && (
                <Button variant="secondary" onClick={() => setDecision({ to: 'INFO_REQUESTED', label: 'Solicitar información' })}>
                  Solicitar información
                </Button>
              )}
              {i.canApprove && ['SUBMITTED', 'IN_REVIEW'].includes(i.status) && (
                <>
                  <Button variant="success" onClick={() => setDecision({ to: 'APPROVED', label: 'Aprobar' })}>
                    Aprobar
                  </Button>
                  <Button variant="danger" onClick={() => setDecision({ to: 'REJECTED', label: 'Rechazar' })}>
                    Rechazar
                  </Button>
                </>
              )}
              {i.canReview && ['APPROVED', 'REJECTED'].includes(i.status) && (
                <Button variant="secondary" onClick={() => transition.mutate({ to: 'CLOSED' })}>
                  Cerrar
                </Button>
              )}
              {i.requiresApproval && <Alert tone="warning">Esta incidencia requiere aprobación.</Alert>}
              {i.timeRecordId && (
                <Link to={`/admin/records/${i.timeRecordId}`} className="text-sm text-brand-700 hover:underline">
                  Ver jornada
                </Link>
              )}
            </div>
          </Card>
        )}
      </div>
      <Modal
        open={Boolean(decision)}
        onClose={() => setDecision(null)}
        title={decision?.label ?? ''}
        footer={
          <Button loading={transition.isPending} onClick={() => transition.mutate({ to: decision!.to, comment: comment || undefined })}>
            Confirmar
          </Button>
        }
      >
        <Field label={decision?.to === 'APPROVED' ? 'Comentario (opcional)' : 'Motivo / información requerida'}>{(id) => <Textarea id={id} value={comment} onChange={(e) => setComment(e.target.value)} />}</Field>
      </Modal>
    </div>
  );
}

export function WorkerIncidentDetail() {
  const { id } = useParams();
  return <IncidentThread id={id!} backTo="/app/requests" />;
}

export function WorkerCorrectionDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [answer, setAnswer] = useState('');
  const { data, isLoading } = useQuery({ queryKey: ['correction', id], queryFn: () => api.get(`/api/v1/corrections/${id}`) });
  const respond = useMutation({ mutationFn: () => api.post(`/api/v1/corrections/${id}/respond`, { comment: answer }), onSuccess: () => (toast.success('Información enviada'), qc.invalidateQueries({ queryKey: ['correction', id] })) });
  const cancel = useMutation({ mutationFn: () => api.post(`/api/v1/corrections/${id}/cancel`), onSuccess: () => qc.invalidateQueries({ queryKey: ['correction', id] }) });
  if (isLoading) return <Spinner />;
  const c = data;
  return (
    <div>
      <PageHeader
        back={
          <Link to="/app/requests" className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500">
            <ChevronLeft className="h-4 w-4" /> Solicitudes
          </Link>
        }
        title={`Corrección del ${fmtDate(c.workDate)}`}
        actions={<RequestStatusBadge status={c.status} />}
      />
      <Card>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-slate-500">Tipo</dt>
            <dd>{{ ADD_EVENT: 'Añadir fichaje', CHANGE_TIME: 'Cambiar hora', VOID_EVENT: 'Anular fichaje' }[c.kind as string]}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Hora</dt>
            <dd>
              {c.previousAt ? `${fmtTime(c.previousAt)} → ` : ''}
              {c.proposedAt ? fmtTime(c.proposedAt) : '—'}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs text-slate-500">Motivo</dt>
            <dd className="whitespace-pre-line">{c.reason}</dd>
          </div>
          {c.reviewerComment && (
            <div className="sm:col-span-2">
              <dt className="text-xs text-slate-500">Respuesta de {c.reviewer ?? 'tu responsable'}</dt>
              <dd>{c.reviewerComment}</dd>
            </div>
          )}
        </dl>
        {c.status === 'INFO_REQUESTED' && (
          <div className="mt-4 space-y-2">
            <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Añade la información solicitada" aria-label="Información" />
            <Button onClick={() => respond.mutate()} disabled={answer.trim().length < 3}>
              Enviar información
            </Button>
          </div>
        )}
        {['PENDING', 'INFO_REQUESTED'].includes(c.status) && (
          <Button variant="ghost" className="mt-4" onClick={() => cancel.mutate()}>
            Cancelar solicitud
          </Button>
        )}
      </Card>
    </div>
  );
}
